import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";

import { makeStyles } from "@mui/styles";
import {
  Box,
  Chip,
  Grid,
  Theme,
  Typography,
  useMediaQuery,
} from "@mui/material";
import {
  GridColumns,
  DataGrid,
  GridRowParams,
  GridActionsCellItem,
  GridRenderCellParams,
} from "@mui/x-data-grid";
import { AddTwoTone, EditTwoTone } from "@mui/icons-material";

import moment from "moment";
import useAxios from "axios-hooks";

import MainCard from "components/mainCard";
import Button from "components/button";
import Loader from "components/loader";
import ExcelExport from "features/components/excelExport";
import { TableNoRowsOverlay } from "components/skeleton/tableNoRowsOverlay";
import { TableListingSkeleton } from "components/skeleton/tableListingSkeleton";

import {
  capitalizeFLetter,
  getLocalStorage,
} from "features/components/helperFunctions";
import {
  headerButtonMobile,
  reservationButtonCss,
} from "features/businessListing/index.styles";
import {
  cardHeaderBtnGridStyle,
  setsTableStyle,
} from "features/products/index.styles";
import AddEditReservations from "./addEditReservations";
import ReservationDetail from "./reservationDetail";
import { API_END_POINTS } from "utils/constants/APIEndPoints";
import { reservationExport } from "utils/constants";
import { DropDownList, ReservationsResponseResult } from "utils/types";
import { dispatch, useSelector } from "store";
import { setDate, toggleDatePicker } from "store/slices/Main";
import { openSnackbar } from "store/slices/snackbar";

const useStyles = makeStyles(() => ({
  reservationIdStyles: {
    paddingLeft: "0px !important",
    paddingRight: "60px !important",
    fontWeight: 500,
  },
  guestsStyles: {
    paddingLeft: "0px !important",
    paddingRight: "60px !important",
  },
}));

export let callGetReservationsAPI: () => void;

const Reservations = () => {
  //======================================= Styling =======================================//

  const isScreenSmall = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );

  const headerButtonStyles = isScreenSmall
    ? headerButtonMobile
    : reservationButtonCss;

  const [reservations, setReservations] = useState<
    ReservationsResponseResult[] | []
  >([]);

  const [branchesList, setBranchesList] = useState<DropDownList[] | []>([]);

  const [selectedReservation, setSelectedReservation] = useState<
    ReservationsResponseResult[] | []
  >([]);

  const [toggleAddEditReservations, setToggleAddEditReservations] =
    useState<boolean>(false);
  const [toggleReservationDetails, setToggleReservationDetails] =
    useState<boolean>(false);

  const { startDate, endDate } = useSelector((reduxState) => reduxState.global);
  const classes = useStyles();
  const { pathname } = useLocation();

  const { eatout_id, user_id } = getLocalStorage();

  //======================================= API Call Payloads =======================================//

  const branchesParams = {
    restaurant_id: `${eatout_id}`,
    admin_id: `${user_id}`,
    uid: `${user_id}`,
    source: "biz",
  };

  // get reservations API call payload
  const getReservationsAPIPayload = () => {
    const formData = new FormData();

    formData.append("offset", "200");
    formData.append("from_date", startDate ? startDate : "");
    formData.append("till_date", endDate ? endDate : "");

    formData.append("eatout_id", `${eatout_id}`);
    formData.append("admin_id", `${user_id}`);
    formData.append("source", "biz");
    return formData;
  };

  // get reservations API call
  const [
    {
      data: allReservations,
      loading: reservationsLoading,
      error: reservationsError,
    },
    getReservationsAPICall,
  ] = useAxios(API_END_POINTS.getReservations, { manual: true });

  // API call to get branches locations
  const [{ error: getBranchesError }, getBranchesAPI] = useAxios(
    { ...API_END_POINTS.getAccessBranches, params: branchesParams },
    { manual: true }
  );

  //======================================= useEffect Hooks =======================================//

  useEffect(() => {
    // calling branches API
    getBranchesAPICall();

    // setting last 30 days date on leaving the reservation module
    return () => {
      dispatch(
        setDate({
          startDate: moment().subtract(2, "days").toDate(),
          endDate: moment().toDate(),
        })
      );
    };
  }, []);

  useEffect(() => {
    if (pathname === "/reservations/expired") {
      // show date picker for expired reservations
      dispatch(toggleDatePicker(true));
    } else {
      // hide date picker for upcoming reservations
      dispatch(toggleDatePicker(false));
    }

    /**
     * calling get reservations API
     * calls api on componentDidMount & on changing the date
     */
    callGetReservationsAPI();
  }, [startDate, endDate]);

  useEffect(() => {
    const start_date =
      pathname === "/reservations/expired"
        ? moment().subtract(30, "days")
        : moment();

    const end_date =
      pathname === "/reservations/expired"
        ? moment().subtract(1, "days")
        : moment().add(30, "days");

    // setting next 30 days on date picker
    dispatch(
      setDate({
        startDate: start_date,
        endDate: end_date,
      })
    );
  }, [pathname]);

  // useEffect to handle API error states
  useEffect(() => {
    if (reservationsError || getBranchesError) {
      // notify user

      dispatch(
        openSnackbar({
          open: true,
          message: reservationsError
            ? reservationsError.message
            : getBranchesError
            ? getBranchesError.message
            : "",
          alert: { color: "error" },
        })
      );
    }
  }, [reservationsError, getBranchesError]);

  useEffect(() => {
    if (isScreenSmall) {
      callGetReservationsAPI();
    }
  }, [isScreenSmall]);
  //======================================= Handler Functions =======================================//

  // function to call reservations API
  callGetReservationsAPI = async () => {
    // API call to get reservations
    const { data } = await getReservationsAPICall({
      data: getReservationsAPIPayload(),
    });

    // status 1 => getting required response from API
    if (data?.status === "1" && Array.isArray(data.result)) {
      setReservations(data.result);
    }
  };

  // API call to get business branches
  const getBranchesAPICall = async () => {
    const { data } = await getBranchesAPI();

    if (data?.length) {
      setBranchesList(
        data.map((branch: { location_address: string; branch_id: string }) => ({
          label: branch.location_address,
          value: branch.branch_id,
        }))
      );
    }
  };

  const reservationDetailsCallback = async () => {
    // API call to get reservations
    const { data } = await getReservationsAPICall({
      data: getReservationsAPIPayload(),
    });

    // status 1 => getting required response from API
    if (data?.status === "1" && Array.isArray(data.result)) {
      setReservations(data.result);

      const filteredReservation =
        selectedReservation.length &&
        data.result.filter(
          (reservation: ReservationsResponseResult) =>
            reservation.reservation_id === selectedReservation[0].reservation_id
        );

      if (!filteredReservation.length) {
        handleToggleReservationDetails();
      }

      setSelectedReservation(filteredReservation);
    }
  };

  const addEditReservationsCallback = (apiCallFlag = true) => {
    handleToggleAddEditReservations();

    /**
     * API call to get updated list of reservations
     * stopping api call on adding reservation (calling api from pusher)
     */
    if (apiCallFlag) {
      callGetReservationsAPI();
    }

    // case of edit reservations
    if (selectedReservation?.length) {
      // notify user
      dispatch(
        openSnackbar({
          open: true,
          message: "Reservation updated successfully",
          alert: {
            color: "success",
          },
        })
      );
    }
  };

  const handleRowClick = (params: GridRowParams) => {
    const filteredReservation = reservations.filter(
      (reservation: ReservationsResponseResult) =>
        reservation.reservation_id === params.id.toString()
    );

    setSelectedReservation(filteredReservation);

    handleToggleReservationDetails();
  };

  const triggerEditAction = (id: string) => {
    const filteredReservation = reservations.filter(
      (reservation: ReservationsResponseResult) =>
        reservation.reservation_id === id
    );

    setSelectedReservation(filteredReservation);

    handleToggleAddEditReservations();
  };

  const actionButtons = (params: GridRowParams) => {
    return [
      <GridActionsCellItem
        icon={<EditTwoTone />}
        label="Edit"
        showInMenu
        onClick={() => triggerEditAction(params.id.toString())}
      />,
    ];
  };

  const styledStatus = (params: GridRenderCellParams) => {
    let color = {
      borderRadius: "100px",
      fontFamily: "Roboto",
      fontStyle: "normal",
      fontWeight: 400,
      fontSize: "13px",
      background: "",
      color: "",
      padding: "",
    };

    switch (params.value) {
      case "Pending": {
        color.background = "#EDE7F6";
        color.color = "#673AB7";
        color.padding = "8px 16px";
        break;
      }
      case "Confirmed": {
        color.background = "#C7FFD6";
        color.color = "#00C853";
        color.padding = "8px 9.5px";
        break;
      }
      case "Cancel": {
        color.background = "#FBE9E7";
        color.color = "#D84315";
        color.padding = "8px 21px";
        break;
      }
      case "Seated": {
        color.background = "#DFF2BF";
        color.padding = "8px 21px";
        break;
      }
      case "Callback": {
        color.background = "#FEEFB3";
        color.padding = "8px 16px";
        break;
      }
      default: {
        //for Served
        color.background = "#caeff2";
        color.padding = "8px 21px";
        break;
      }
    }

    return (
      <Chip
        label={params.value}
        sx={{
          "& .MuiChip-label": {
            p: "unset",
          },
          ...color,
        }}
      />
    );
  };

  // format name
  const formatName = (params: GridRenderCellParams) => {
    return (
      <Typography sx={{ fontWeight: 500, color: "#212121" }}>
        {capitalizeFLetter(params.row.user_name)}
      </Typography>
    );
  };

  // format date & time
  const formatDate = (params: GridRenderCellParams) => {
    const { date, time } = params.row;

    // fallback text if both date and time are missing
    let schedule = "-";

    // verify date & time and display accordingly
    if (date && time) {
      const dateTime = moment(`${date} ${time}`, "YYYY-MM-DD h:mm A");
      schedule = dateTime.format("MMM Do, YYYY hh:mm a");
    } else if (date) {
      schedule = moment(date).format("MMM Do, YYYY");
    } else if (time) {
      schedule = moment(time, "h:mm A").format("HH:mm");
    }

    return (
      <Typography
        style={{
          whiteSpace: "normal",
          wordWrap: "break-word",
          color: "#212121",
        }}
      >
        {schedule}
      </Typography>
    );
  };

  const columns: GridColumns = [
    {
      field: "reservation_id",
      headerName: "Reservation #",
      align: "right",
      headerAlign: "right",
      headerClassName: classes.reservationIdStyles,
      cellClassName: classes.reservationIdStyles,
      flex: 0.62,
      minWidth: 150,
      maxWidth: 150,
      sortable: false,
    },
    {
      field: "date",
      headerName: "Date",
      flex: 1,
      sortable: false,
      minWidth: 120,
      renderCell: formatDate,
    },
    {
      field: "user_name",
      headerName: "Name",
      flex: 1.1,
      sortable: false,
      minWidth: 180,
      renderCell: formatName,
    },
    {
      field: "guests",
      headerName: "Guests",
      align: "right",
      headerAlign: "right",
      headerClassName: classes.guestsStyles,
      cellClassName: classes.guestsStyles,
      flex: 0.7,
      minWidth: 50,
      sortable: false,
    },
    {
      field: "status",
      headerName: "Status",
      flex: 0.8,
      minWidth: 120,
      sortable: false,
      renderCell: styledStatus,
    },
    {
      field: "action",
      headerName: "Actions",
      type: "actions",
      align: "right",
      headerAlign: "right",
      flex: 0.5,
      minWidth: 80,
      sortable: false,
      getActions: actionButtons,
    },
  ];

  // const handleSearchChange = debounce((e: { target: { value: string } }) => {
  //   if (e.target.value) {
  //     const filteredReservation = allReservations?.data.filter(
  //       (reservation: ReservationsResponseResult) =>
  //         reservation.name
  //           .toLowerCase()
  //           .includes(e.target.value.toLowerCase().trim())
  //     );
  //     setReservations(filteredReservation);
  //   } else {
  //     // API call to get updated list of reservations
  //     callGetReservationsAPI();
  //   }
  // }, 300);

  const customNoRowsOverlay = () => {
    return <TableNoRowsOverlay />;
  };

  const handleToggleReservationDetails = () => {
    setToggleReservationDetails((state) => !state);
  };

  const handleToggleAddEditReservations = () => {
    // reset state when drawer is closed
    if (selectedReservation?.length) {
      setSelectedReservation([]);
    }

    setToggleAddEditReservations((state) => !state);
  };

  return (
    <>
      {reservationsLoading && <Loader />}

      {toggleAddEditReservations && (
        <AddEditReservations
          branchesList={branchesList}
          selectedReservation={selectedReservation}
          addEditReservationsCallback={addEditReservationsCallback}
          toggleAddEditReservations={toggleAddEditReservations}
          handleToggleAddEditReservations={handleToggleAddEditReservations}
          component={"Drawer"}
        />
      )}

      {toggleReservationDetails && (
        <ReservationDetail
          branchesList={branchesList}
          selectedReservation={selectedReservation}
          reservationDetailsCallback={reservationDetailsCallback}
          toggleReservationDetails={toggleReservationDetails}
          handleToggleReservationDetails={handleToggleReservationDetails}
        />
      )}

      {/* reservations listing  */}
      <MainCard
        title={
          <Grid container>
            <Grid
              item
              xs={12}
              sm={6}
              sx={{ display: "flex", alignItems: "center" }}
            >
              <Typography variant={isScreenSmall ? "h4" : "h3"}>
                Reservations
              </Typography>

              {/*  TODO: dependency on API end */}
              {/* <TextField
                type="search"
                placeholder={"Search Reservations"}
                onChange={handleSearchChange}
                sx={searchFieldStyle}
              /> */}
            </Grid>

            <Grid
              item
              xs={12}
              sm={6}
              sx={{
                ...cardHeaderBtnGridStyle,
                flexDirection: { xs: "column-reverse", sm: "row" },
              }}
            >
              <ExcelExport
                tableData={reservationExport}
                listingData={reservations}
                exportType={"ListingExport"}
                buttonText={"Export Reservations"}
                disabled={reservationsLoading || reservations.length < 1}
              />
              {pathname !== "/reservations/expired" && (
                <Button
                  variant={"contained"}
                  color={"secondary"}
                  startIcon={<AddTwoTone />}
                  onClick={handleToggleAddEditReservations}
                  disabled={reservationsLoading}
                  sx={headerButtonStyles}
                >
                  Add Reservation
                </Button>
              )}
            </Grid>
          </Grid>
        } // MainCard opening tag closed here
      >
        <Box sx={setsTableStyle}>
          {!allReservations ? (
            <TableListingSkeleton />
          ) : (
            //  listing reservations
            <DataGrid
              rows={reservations}
              columns={columns}
              getRowId={(row) => row.reservation_id}
              autoHeight
              rowHeight={76}
              onRowClick={handleRowClick}
              disableColumnMenu
              disableSelectionOnClick
              hideFooter
              components={{
                NoRowsOverlay: customNoRowsOverlay,
              }}
            />
          )}
        </Box>
      </MainCard>
    </>
  );
};

export default Reservations;
